import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';

import { datadogRum } from '@datadog/browser-rum';

import SignInPage from './features/auth/components/SignIn';
import HomePage from "./features/auth/components/Home";
import ErrorBoundary from './components/ErrorBoundary';

// Get runtime configuration from window object (loaded via config.js and env-config.js)
declare global {
  interface Window {
    DATADOG_CONFIG: {
      DATADOG_APP_ID: string;
      DATADOG_CLIENT_TOKEN: string;
      DATADOG_SITE: string;
      DATADOG_SERVICE: string;
      DATADOG_SESSION_SAMPLE_RATE: number;
      DATADOG_SESSION_REPLAY_SAMPLE_RATE: number;
      DATADOG_TRACK_USER_INTERACTIONS: boolean;
      DATADOG_TRACK_RESOURCES: boolean;
      DATADOG_TRACK_LONG_TASKS: boolean;
    };
    ENV_CONFIG: {
      ENVIRONMENT: string;
      // Per-environment API endpoint, injected at deploy time via *-env-config.js.
      API_URL?: string;
      // Optional per-environment overrides for the static config.js defaults
      // (e.g. a separate Datadog RUM application for production).
      DATADOG_APP_ID?: string;
      DATADOG_CLIENT_TOKEN?: string;
    };
  }
}

// Merge static Datadog config with environment config
const datadogConfig = window.DATADOG_CONFIG || {
  DATADOG_APP_ID: '86559003-6e29-4280-a10b-412605a9baa6',
  DATADOG_CLIENT_TOKEN: 'pub9a037c40686933e248c5fd337e631890',
  DATADOG_SITE: 'datadoghq.com',
  DATADOG_SERVICE: 'na-login-page',
  DATADOG_SESSION_SAMPLE_RATE: 100,
  DATADOG_SESSION_REPLAY_SAMPLE_RATE: 100,
  DATADOG_TRACK_USER_INTERACTIONS: true,
  DATADOG_TRACK_RESOURCES: true,
  DATADOG_TRACK_LONG_TASKS: true
};

const envConfig = window.ENV_CONFIG || {
  ENVIRONMENT: 'development'
};

const config = {
  ...datadogConfig,
  ...envConfig
};

// Initialize Datadog RUM with error handling to ensure page loads even if RUM fails
try {
  datadogRum.init({
    applicationId: config.DATADOG_APP_ID,
    clientToken: config.DATADOG_CLIENT_TOKEN,
    site: config.DATADOG_SITE,
    service: config.DATADOG_SERVICE,
    env: config.ENVIRONMENT,
    // Release version baked in at build time (git describe) by the deploy pipeline
    version: process.env.REACT_APP_VERSION,
    sessionSampleRate: config.DATADOG_SESSION_SAMPLE_RATE,
    sessionReplaySampleRate: config.DATADOG_SESSION_REPLAY_SAMPLE_RATE,
    trackResources: config.DATADOG_TRACK_RESOURCES,
    trackLongTasks: config.DATADOG_TRACK_LONG_TASKS,
    trackUserInteractions: config.DATADOG_TRACK_USER_INTERACTIONS,
    // Enable automatic route tracking
    trackViewsManually: false,
    // Privacy settings for session replay
    defaultPrivacyLevel: 'mask-user-input',
  });
} catch (error) {
  // Log the error but don't prevent the app from loading
  console.warn('Datadog RUM initialization failed:', error);
  // In production, the app continues to work normally even if monitoring fails
}

// Component to track route changes
const RouteTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Track route changes in Datadog
    if (datadogRum) {
      datadogRum.addAction('route_change', {
        path: location.pathname,
        search: location.search,
        hash: location.hash,
      });
    }
  }, [location]);

  return <>{children}</>;
};

const App = () => {
    return (
        <ErrorBoundary>
            <Router>
                <RouteTracker>
                    <Routes>
                        <Route path="/" element={ <SignInPage />} />
                        <Route path="/home" element={ <HomePage />} />
                    </Routes>
                </RouteTracker>
            </Router>
        </ErrorBoundary>
    );
};


export default App;
